/**
 * LegalComplianceEngine - Court formatting rules and legal document compliance
 * 
 * Ensures legal documents meet court-specific formatting requirements,
 * jurisdiction rules, and professional legal document standards.
 */

import { LocalDocumentState } from '../types/DocumentTypes';

interface CourtRule {
  ruleId: string;
  jurisdiction: string;
  court: string;
  ruleType: 'formatting' | 'content' | 'filing' | 'citation' | 'margin' | 'font' | 'spacing';
  title: string;
  description: string;
  requirement: string;
  severity: 'mandatory' | 'recommended' | 'optional';
  penalty?: string;
  citation?: string;
}

interface ComplianceViolation {
  ruleId: string;
  violationType: string;
  severity: 'error' | 'warning' | 'info';
  message: string;
  location: { start: number; end: number };
  suggestedFix: string;
  autoFixAvailable: boolean;
  courtRule: CourtRule;
}

interface JurisdictionProfile {
  jurisdiction: string;
  courts: string[];
  rules: CourtRule[];
  defaultFont: string;
  defaultFontSize: number;
  lineSpacing: number;
  marginRequirements: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
  pageNumbering: {
    required: boolean;
    position: 'top' | 'bottom' | 'header' | 'footer';
    format: string;
  };
  lineNumbering?: {
    required: boolean;
    everyNthLine: number;
    leftMargin: number;
  };
}

interface DocumentTypeRequirements {
  documentType: string;
  jurisdiction: string;
  requiredSections: string[];
  forbiddenContent: string[];
  mandatoryFormatting: CourtRule[];
  templateStructure?: any;
}

export class LegalComplianceEngine {
  private jurisdictionProfiles: Map<string, JurisdictionProfile> = new Map();
  private documentTypeRequirements: Map<string, DocumentTypeRequirements> = new Map();
  private courtRules: Map<string, CourtRule> = new Map();
  private complianceCache: Map<string, ComplianceViolation[]> = new Map();
  
  // Compliance configuration
  private enableRealTimeValidation: boolean = true;
  private autoFixMinorViolations: boolean = true;
  private _strictMode: boolean = false; // For court filing preparation
  private validationDebounce: number = 1000; // 1 second
  
  private validationTimer: NodeJS.Timeout | null = null;

  constructor() {
    this.initializeJurisdictions();
    this.initializeDocumentTypes();
    this.initializeCourtRules();
  }

  // ============================================================================
  // COMPLIANCE VALIDATION
  // ============================================================================

  /**
   * Validate document compliance for specific jurisdiction and document type
   */
  async validateCompliance(
    documentState: LocalDocumentState,
    jurisdiction: string,
    documentType: string,
    targetCourt?: string
  ): Promise<ComplianceViolation[]> {
    console.log(`[LegalComplianceEngine] Validating compliance for ${jurisdiction} ${documentType}`);
    
    const violations: ComplianceViolation[] = [];
    
    // Get applicable rules
    const applicableRules = this.getApplicableRules(jurisdiction, documentType, targetCourt);
    
    // Validate against each rule
    for (const rule of applicableRules) {
      const ruleViolations = await this.validateRule(documentState, rule);
      violations.push(...ruleViolations);
    }
    
    // Cache results
    const cacheKey = `${jurisdiction}:${documentType}:${documentState.version}`;
    this.complianceCache.set(cacheKey, violations);
    
    // Log compliance status
    if (violations.length === 0) {
      console.log(`[LegalComplianceEngine] Document compliant with ${applicableRules.length} rules`);
    } else {
      const errors = violations.filter(v => v.severity === 'error').length;
      const warnings = violations.filter(v => v.severity === 'warning').length;
      console.warn(`[LegalComplianceEngine] Compliance issues: ${errors} errors, ${warnings} warnings`);
    }
    
    return violations;
  }

  /**
   * Validate specific court rule
   */
  private async validateRule(
    documentState: LocalDocumentState,
    rule: CourtRule
  ): Promise<ComplianceViolation[]> {
    const violations: ComplianceViolation[] = [];
    
    switch (rule.ruleType) {
      case 'formatting':
        violations.push(...await this.validateFormattingRule(documentState, rule));
        break;
        
      case 'content':
        violations.push(...await this.validateContentRule(documentState, rule));
        break;
        
      case 'margin':
        violations.push(...await this.validateMarginRule(documentState, rule));
        break;
        
      case 'font':
        violations.push(...await this.validateFontRule(documentState, rule));
        break;
        
      case 'spacing':
        violations.push(...await this.validateSpacingRule(documentState, rule));
        break;
        
      case 'citation':
        violations.push(...await this.validateCitationRule(documentState, rule));
        break;
        
      case 'filing':
        violations.push(...await this.validateFilingRule(documentState, rule));
        break;
        
      default:
        console.warn(`[LegalComplianceEngine] Unknown rule type: ${rule.ruleType}`);
    }
    
    return violations;
  }

  // ============================================================================
  // RULE VALIDATION METHODS
  // ============================================================================

  private async validateFormattingRule(
    documentState: LocalDocumentState,
    rule: CourtRule
  ): Promise<ComplianceViolation[]> {
    const violations: ComplianceViolation[] = [];
    
    // Example: Double-spacing requirement
    if (rule.ruleId === 'double_spacing_required') {
      const lineSpacing = this.extractLineSpacing(documentState);
      if (lineSpacing < 1.8) { // Allow slight variance
        violations.push({
          ruleId: rule.ruleId,
          violationType: 'line_spacing',
          severity: rule.severity === 'mandatory' ? 'error' : 'warning',
          message: `Line spacing must be double-spaced (2.0), currently ${lineSpacing}`,
          location: { start: 0, end: documentState.text.length },
          suggestedFix: 'Change line spacing to 2.0 (double-spaced)',
          autoFixAvailable: true,
          courtRule: rule
        });
      }
    }
    
    // Example: Page numbering requirement
    if (rule.ruleId === 'page_numbering_required') {
      const hasPageNumbers = this.checkPageNumbering(documentState);
      if (!hasPageNumbers) {
        violations.push({
          ruleId: rule.ruleId,
          violationType: 'page_numbering',
          severity: 'error',
          message: 'Page numbering is required for court filings',
          location: { start: 0, end: 0 },
          suggestedFix: 'Enable page numbering in document settings',
          autoFixAvailable: true,
          courtRule: rule
        });
      }
    }
    
    return violations;
  }

  private async validateContentRule(
    documentState: LocalDocumentState,
    rule: CourtRule
  ): Promise<ComplianceViolation[]> {
    const violations: ComplianceViolation[] = [];
    
    // Example: Required signature block
    if (rule.ruleId === 'signature_block_required') {
      const hasSignatureBlock = this.checkSignatureBlock(documentState.text);
      if (!hasSignatureBlock) {
        violations.push({
          ruleId: rule.ruleId,
          violationType: 'missing_signature',
          severity: 'error',
          message: 'Document must include attorney signature block',
          location: { start: documentState.text.length, end: documentState.text.length },
          suggestedFix: 'Add attorney signature block at document end',
          autoFixAvailable: true,
          courtRule: rule
        });
      }
    }
    
    // Example: Certificate of service
    if (rule.ruleId === 'certificate_of_service_required') {
      const hasCertificate = this.checkCertificateOfService(documentState.text);
      if (!hasCertificate) {
        violations.push({
          ruleId: rule.ruleId,
          violationType: 'missing_certificate',
          severity: 'warning',
          message: 'Certificate of service recommended for court filings',
          location: { start: documentState.text.length, end: documentState.text.length },
          suggestedFix: 'Add certificate of service',
          autoFixAvailable: true,
          courtRule: rule
        });
      }
    }
    
    return violations;
  }

  private async validateMarginRule(
    documentState: LocalDocumentState,
    rule: CourtRule
  ): Promise<ComplianceViolation[]> {
    const violations: ComplianceViolation[] = [];
    
    // Example: Minimum margin requirements
    if (rule.ruleId === 'minimum_margins') {
      const margins = this.extractMargins(documentState);
      const profile = this.getJurisdictionProfile((documentState.jurisdiction || 'federal') as string);
      
      if (profile) {
        const required = profile.marginRequirements;
        
        if (margins.left < required.left) {
          violations.push({
            ruleId: rule.ruleId,
            violationType: 'left_margin',
            severity: 'error',
            message: `Left margin must be at least ${required.left}" (currently ${margins.left}")`,
            location: { start: 0, end: 0 },
            suggestedFix: `Increase left margin to ${required.left}"`,
            autoFixAvailable: true,
            courtRule: rule
          });
        }
        
        // Similar checks for other margins...
      }
    }
    
    return violations;
  }

  private async validateFontRule(
    documentState: LocalDocumentState,
    rule: CourtRule
  ): Promise<ComplianceViolation[]> {
    const violations: ComplianceViolation[] = [];
    
    // Example: Font requirements
    if (rule.ruleId === 'approved_fonts_only') {
      const fonts = this.extractFonts(documentState);
      const approvedFonts = ['Times New Roman', 'Arial', 'Courier New', 'Calibri'];
      
      for (const font of fonts) {
        if (!approvedFonts.includes(font.name)) {
          violations.push({
            ruleId: rule.ruleId,
            violationType: 'unapproved_font',
            severity: 'error',
            message: `Font "${font.name}" not approved for court filings`,
            location: font.location,
            suggestedFix: `Change to approved font (${approvedFonts.join(', ')})`,
            autoFixAvailable: true,
            courtRule: rule
          });
        }
      }
    }
    
    return violations;
  }

  private async validateSpacingRule(
    documentState: LocalDocumentState,
    rule: CourtRule
  ): Promise<ComplianceViolation[]> {
    const violations: ComplianceViolation[] = [];
    
    // Example: Paragraph spacing
    if (rule.ruleId === 'paragraph_spacing') {
      const spacingIssues = this.checkParagraphSpacing(documentState.text);
      
      for (const issue of spacingIssues) {
        violations.push({
          ruleId: rule.ruleId,
          violationType: 'paragraph_spacing',
          severity: 'warning',
          message: issue.message,
          location: issue.location,
          suggestedFix: issue.fix,
          autoFixAvailable: true,
          courtRule: rule
        });
      }
    }
    
    return violations;
  }

  private async validateCitationRule(
    documentState: LocalDocumentState,
    rule: CourtRule
  ): Promise<ComplianceViolation[]> {
    const violations: ComplianceViolation[] = [];
    
    // Example: Citation format validation
    if (rule.ruleId === 'bluebook_citations') {
      const citations = this.extractCitations(documentState.text);
      
      for (const citation of citations) {
        const isValid = this.validateBluebookFormat(citation.text);
        if (!isValid) {
          violations.push({
            ruleId: rule.ruleId,
            violationType: 'citation_format',
            severity: 'warning',
            message: `Citation may not follow Bluebook format: "${citation.text}"`,
            location: citation.location,
            suggestedFix: 'Review citation format against Bluebook rules',
            autoFixAvailable: false,
            courtRule: rule
          });
        }
      }
    }
    
    return violations;
  }

  private async validateFilingRule(
    documentState: LocalDocumentState,
    rule: CourtRule
  ): Promise<ComplianceViolation[]> {
    const violations: ComplianceViolation[] = [];
    
    // Example: Electronic filing requirements
    if (rule.ruleId === 'electronic_filing_format') {
      const isEFilingCompliant = this.checkEFilingCompliance(documentState);
      if (!isEFilingCompliant.compliant) {
        violations.push({
          ruleId: rule.ruleId,
          violationType: 'efiling_format',
          severity: 'error',
          message: isEFilingCompliant.reason || 'E-filing compliance issue',
          location: { start: 0, end: documentState.text.length },
          suggestedFix: isEFilingCompliant.fix || 'Review e-filing requirements',
          autoFixAvailable: isEFilingCompliant.autoFixable,
          courtRule: rule
        });
      }
    }
    
    return violations;
  }

  // ============================================================================
  // JURISDICTION MANAGEMENT
  // ============================================================================

  private initializeJurisdictions(): void {
    // Federal jurisdiction
    this.addJurisdictionProfile({
      jurisdiction: 'federal',
      courts: ['US District Court', 'US Court of Appeals', 'US Supreme Court'],
      rules: [],
      defaultFont: 'Times New Roman',
      defaultFontSize: 12,
      lineSpacing: 2.0,
      marginRequirements: {
        top: 1.0,
        bottom: 1.0,
        left: 1.5,
        right: 1.0
      },
      pageNumbering: {
        required: true,
        position: 'bottom',
        format: 'Page {page}'
      }
    });
    
    // California state courts
    this.addJurisdictionProfile({
      jurisdiction: 'california',
      courts: ['Superior Court', 'Court of Appeal', 'Supreme Court'],
      rules: [],
      defaultFont: 'Arial',
      defaultFontSize: 12,
      lineSpacing: 2.0,
      marginRequirements: {
        top: 1.0,
        bottom: 1.0,
        left: 1.25,
        right: 1.0
      },
      pageNumbering: {
        required: true,
        position: 'bottom',
        format: '{page}'
      },
      lineNumbering: {
        required: true,
        everyNthLine: 5,
        leftMargin: 0.5
      }
    });
    
    // New York state courts
    this.addJurisdictionProfile({
      jurisdiction: 'new_york',
      courts: ['Supreme Court', 'Appellate Division', 'Court of Appeals'],
      rules: [],
      defaultFont: 'Times New Roman',
      defaultFontSize: 12,
      lineSpacing: 2.0,
      marginRequirements: {
        top: 1.0,
        bottom: 1.0,
        left: 1.5,
        right: 1.0
      },
      pageNumbering: {
        required: true,
        position: 'top',
        format: 'Page {page} of {total}'
      }
    });
    
    console.log(`[LegalComplianceEngine] Initialized ${this.jurisdictionProfiles.size} jurisdictions`);
  }

  private initializeDocumentTypes(): void {
    // Motion documents
    this.addDocumentTypeRequirements({
      documentType: 'motion',
      jurisdiction: 'federal',
      requiredSections: ['Caption', 'Introduction', 'Statement of Facts', 'Argument', 'Conclusion', 'Signature'],
      forbiddenContent: ['Personal attacks', 'Irrelevant arguments'],
      mandatoryFormatting: []
    });
    
    // Brief documents
    this.addDocumentTypeRequirements({
      documentType: 'brief',
      jurisdiction: 'federal',
      requiredSections: ['Table of Contents', 'Table of Authorities', 'Statement of Issues', 'Statement of Facts', 'Argument', 'Conclusion'],
      forbiddenContent: ['Unsupported assertions', 'Personal attacks'],
      mandatoryFormatting: []
    });
    
    // Pleading documents
    this.addDocumentTypeRequirements({
      documentType: 'pleading',
      jurisdiction: 'federal',
      requiredSections: ['Caption', 'Parties', 'Jurisdiction', 'Facts', 'Claims', 'Prayer for Relief'],
      forbiddenContent: ['Conclusory statements without facts'],
      mandatoryFormatting: []
    });
    
    console.log(`[LegalComplianceEngine] Initialized ${this.documentTypeRequirements.size} document types`);
  }

  private initializeCourtRules(): void {
    // Federal Rules of Civil Procedure formatting
    this.addCourtRule({
      ruleId: 'frcp_10_form',
      jurisdiction: 'federal',
      court: 'US District Court',
      ruleType: 'formatting',
      title: 'FRCP 10 - Form of Pleadings',
      description: 'Pleadings must be on 8.5x11 paper with specific formatting',
      requirement: 'Double-spaced text, 12pt font, 1.5" left margin',
      severity: 'mandatory',
      penalty: 'Document may be rejected',
      citation: 'Fed. R. Civ. P. 10(a)'
    });
    
    // California Code of Civil Procedure
    this.addCourtRule({
      ruleId: 'ccp_2.100_format',
      jurisdiction: 'california',
      court: 'Superior Court',
      ruleType: 'formatting',
      title: 'CCP 2.100 - Format Requirements',
      description: 'Documents must meet specific format requirements',
      requirement: 'Double-spaced, numbered lines every 5 lines, 1.25" margins',
      severity: 'mandatory',
      penalty: 'Document rejection',
      citation: 'Cal. Code Civ. Proc. § 2.100'
    });
    
    // Bluebook citation requirements
    this.addCourtRule({
      ruleId: 'bluebook_citations',
      jurisdiction: 'federal',
      court: 'all',
      ruleType: 'citation',
      title: 'Bluebook Citation Format',
      description: 'Legal citations must follow Bluebook format',
      requirement: 'Proper case citation format with parallel citations',
      severity: 'recommended',
      citation: 'The Bluebook: A Uniform System of Citation'
    });
    
    console.log(`[LegalComplianceEngine] Initialized ${this.courtRules.size} court rules`);
  }

  // ============================================================================
  // AUTO-FIX CAPABILITIES
  // ============================================================================

  /**
   * Automatically fix minor compliance violations
   */
  async autoFixViolations(
    documentState: LocalDocumentState,
    violations: ComplianceViolation[]
  ): Promise<{
    fixedViolations: ComplianceViolation[];
    remainingViolations: ComplianceViolation[];
    documentChanges: any[];
  }> {
    const fixedViolations: ComplianceViolation[] = [];
    const remainingViolations: ComplianceViolation[] = [];
    const documentChanges: any[] = [];
    
    for (const violation of violations) {
      if (violation.autoFixAvailable && this.autoFixMinorViolations) {
        const fix = await this.applyAutoFix(violation, documentState);
        
        if (fix.success) {
          fixedViolations.push(violation);
          documentChanges.push(fix.change);
        } else {
          remainingViolations.push(violation);
        }
      } else {
        remainingViolations.push(violation);
      }
    }
    
    console.log(`[LegalComplianceEngine] Auto-fixed ${fixedViolations.length}/${violations.length} violations`);
    
    return { fixedViolations, remainingViolations, documentChanges };
  }

  private async applyAutoFix(
    violation: ComplianceViolation,
    documentState: LocalDocumentState
  ): Promise<{ success: boolean; change?: any }> {
    switch (violation.violationType) {
      case 'line_spacing':
        return await this.fixLineSpacing(documentState);
        
      case 'page_numbering':
        return await this.fixPageNumbering(documentState);
        
      case 'missing_signature':
        return await this.addSignatureBlock(documentState);
        
      case 'missing_certificate':
        return await this.addCertificateOfService(documentState);
        
      default:
        return { success: false };
    }
  }

  private async fixLineSpacing(documentState: LocalDocumentState): Promise<{ success: boolean; change?: any }> {
    // Apply double-spacing to document
    const change = {
      type: 'format_change',
      property: 'lineSpacing',
      oldValue: this.extractLineSpacing(documentState),
      newValue: 2.0
    };
    
    return { success: true, change };
  }

  private async fixPageNumbering(_documentState: LocalDocumentState): Promise<{ success: boolean; change?: any }> {
    // Enable page numbering
    const change = {
      type: 'format_change',
      property: 'pageNumbering',
      oldValue: false,
      newValue: true
    };
    
    return { success: true, change };
  }

  private async addSignatureBlock(documentState: LocalDocumentState): Promise<{ success: boolean; change?: any }> {
    const attorney = documentState.attorney;
    const signatureBlock = `\n\nRespectfully submitted,\n\n_________________________\n${attorney.name}\n${attorney.title}\n${attorney.barNumber ? `Bar No. ${attorney.barNumber}` : ''}\n${attorney.firm}\n${attorney.address}\n${attorney.phone}\n${attorney.email}`;
    
    const change = {
      type: 'text_insertion',
      position: documentState.text.length,
      content: signatureBlock
    };
    
    return { success: true, change };
  }

  private async addCertificateOfService(documentState: LocalDocumentState): Promise<{ success: boolean; change?: any }> {
    const certificate = `\n\nCERTIFICATE OF SERVICE\n\nI hereby certify that a true and correct copy of the foregoing was served upon all parties by the method(s) indicated below:\n\n[ ] U.S. Mail\n[ ] Hand delivery\n[ ] Electronic filing\n[ ] Email\n\nThis _____ day of __________, 20___.\n\n_________________________\n${documentState.attorney.name}`;
    
    const change = {
      type: 'text_insertion',
      position: documentState.text.length,
      content: certificate
    };
    
    return { success: true, change };
  }

  // ============================================================================
  // REAL-TIME COMPLIANCE MONITORING
  // ============================================================================

  /**
   * Start real-time compliance monitoring
   */
  startRealTimeMonitoring(
    documentEngine: any,
    jurisdiction: string = 'federal',
    documentType: string = 'document'
  ): void {
    if (!this.enableRealTimeValidation) return;
    
    // Monitor document changes
    documentEngine.on('textChanged', () => {
      this.scheduleComplianceCheck(documentEngine, jurisdiction, documentType);
    });
    
    documentEngine.on('formatChanged', () => {
      this.scheduleComplianceCheck(documentEngine, jurisdiction, documentType);
    });
    
    console.log('[LegalComplianceEngine] Started real-time compliance monitoring');
  }

  private scheduleComplianceCheck(
    documentEngine: any,
    jurisdiction: string,
    documentType: string
  ): void {
    // Debounced compliance check
    if (this.validationTimer) {
      clearTimeout(this.validationTimer);
    }
    
    this.validationTimer = setTimeout(async () => {
      const documentState = documentEngine.getState();
      const violations = await this.validateCompliance(documentState, jurisdiction, documentType);
      
      // Emit compliance status
      const event = new CustomEvent('edgedocs-compliance-status', {
        detail: { violations, jurisdiction, documentType }
      });
      window.dispatchEvent(event);
      
    }, this.validationDebounce);
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  private getApplicableRules(
    jurisdiction: string,
    documentType: string,
    targetCourt?: string
  ): CourtRule[] {
    const rules: CourtRule[] = [];
    
    // Get jurisdiction-specific rules
    for (const rule of this.courtRules.values()) {
      if (rule.jurisdiction === jurisdiction || rule.jurisdiction === 'all') {
        if (!targetCourt || rule.court === targetCourt || rule.court === 'all') {
          rules.push(rule);
        }
      }
    }
    
    // Add document type specific rules
    const docTypeReqs = this.documentTypeRequirements.get(`${documentType}:${jurisdiction}`);
    if (docTypeReqs) {
      rules.push(...docTypeReqs.mandatoryFormatting);
    }
    
    // Sort by severity (mandatory first)
    return rules.sort((a, b) => {
      const severityOrder = { mandatory: 0, recommended: 1, optional: 2 };
      return severityOrder[a.severity] - severityOrder[b.severity];
    });
  }

  private extractLineSpacing(_documentState: LocalDocumentState): number {
    // Extract line spacing from document formatting
    // Placeholder - will integrate with actual formatting system
    return 1.0; // Default single spacing
  }

  private extractMargins(_documentState: LocalDocumentState): {
    top: number; bottom: number; left: number; right: number;
  } {
    // Extract margins from document formatting
    // Placeholder - will integrate with actual formatting system
    return { top: 1.0, bottom: 1.0, left: 1.0, right: 1.0 };
  }

  private extractFonts(documentState: LocalDocumentState): Array<{
    name: string;
    size: number;
    location: { start: number; end: number };
  }> {
    // Extract font information from document
    // Placeholder - will integrate with actual formatting system
    return [{ name: 'Times New Roman', size: 12, location: { start: 0, end: documentState.text.length } }];
  }

  private checkPageNumbering(_documentState: LocalDocumentState): boolean {
    // Check if document has page numbering enabled
    // Placeholder - will integrate with actual page setup
    return false;
  }

  private checkSignatureBlock(text: string): boolean {
    // Check for attorney signature block
    const signaturePatterns = [
      /respectfully submitted/i,
      /s\/.*attorney/i,
      /bar no\./i
    ];
    
    return signaturePatterns.some(pattern => pattern.test(text));
  }

  private checkCertificateOfService(text: string): boolean {
    // Check for certificate of service
    return /certificate of service/i.test(text);
  }

  private checkParagraphSpacing(text: string): Array<{
    message: string;
    location: { start: number; end: number };
    fix: string;
  }> {
    const issues: Array<{
      message: string;
      location: { start: number; end: number };
      fix: string;
    }> = [];
    
    // Check for excessive line breaks
    const excessiveBreaks = text.match(/\n{3,}/g);
    if (excessiveBreaks) {
      issues.push({
        message: 'Excessive line breaks detected',
        location: { start: 0, end: text.length },
        fix: 'Remove extra line breaks'
      });
    }
    
    return issues;
  }

  private extractCitations(text: string): Array<{
    text: string;
    location: { start: number; end: number };
  }> {
    const citations: Array<{
      text: string;
      location: { start: number; end: number };
    }> = [];
    
    // Simple citation detection patterns
    const citationPatterns = [
      /\d+\s+[A-Z][a-z\s]+\d+/g,  // Basic case citation
      /\d+\s+F\.\d*d?\s+\d+/g,    // Federal reporter
      /\d+\s+U\.S\.\s+\d+/g       // US Reports
    ];
    
    for (const pattern of citationPatterns) {
      let match;
      while ((match = pattern.exec(text)) !== null) {
        citations.push({
          text: match[0],
          location: { start: match.index, end: match.index + match[0].length }
        });
      }
    }
    
    return citations;
  }

  private validateBluebookFormat(citation: string): boolean {
    // Basic Bluebook validation
    // Will be enhanced with comprehensive citation parsing
    return /\d+.*\d+/.test(citation); // Very basic check
  }

  private checkEFilingCompliance(_documentState: LocalDocumentState): {
    compliant: boolean;
    reason?: string;
    fix?: string;
    autoFixable: boolean;
  } {
    // Check electronic filing compliance
    // Placeholder implementation
    return { compliant: true, autoFixable: false };
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  /**
   * Get jurisdiction profile
   */
  getJurisdictionProfile(jurisdiction: string): JurisdictionProfile | null {
    return this.jurisdictionProfiles.get(jurisdiction) || null;
  }

  /**
   * Get available jurisdictions
   */
  getAvailableJurisdictions(): string[] {
    return Array.from(this.jurisdictionProfiles.keys());
  }

  /**
   * Get document type requirements
   */
  getDocumentTypeRequirements(documentType: string, jurisdiction: string): DocumentTypeRequirements | null {
    return this.documentTypeRequirements.get(`${documentType}:${jurisdiction}`) || null;
  }

  /**
   * Enable/disable strict compliance mode
   */
  setStrictMode(enabled: boolean): void {
    this._strictMode = enabled;
    console.log(`[LegalComplianceEngine] Strict mode ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Get compliance statistics
   */
  getComplianceStatistics(): {
    totalValidations: number;
    violationsByType: { [type: string]: number };
    violationsBySeverity: { [severity: string]: number };
    autoFixSuccessRate: number;
  } {
    const stats = {
      totalValidations: this.complianceCache.size,
      violationsByType: {} as { [type: string]: number },
      violationsBySeverity: {} as { [severity: string]: number },
      autoFixSuccessRate: 0
    };
    
    for (const violations of this.complianceCache.values()) {
      for (const violation of violations) {
        stats.violationsByType[violation.violationType] = 
          (stats.violationsByType[violation.violationType] || 0) + 1;
        
        stats.violationsBySeverity[violation.severity] = 
          (stats.violationsBySeverity[violation.severity] || 0) + 1;
      }
    }
    
    return stats;
  }

  // ============================================================================
  // PRIVATE HELPERS
  // ============================================================================

  private addJurisdictionProfile(profile: JurisdictionProfile): void {
    this.jurisdictionProfiles.set(profile.jurisdiction, profile);
  }

  private addDocumentTypeRequirements(requirements: DocumentTypeRequirements): void {
    const key = `${requirements.documentType}:${requirements.jurisdiction}`;
    this.documentTypeRequirements.set(key, requirements);
  }

  private addCourtRule(rule: CourtRule): void {
    this.courtRules.set(rule.ruleId, rule);
  }

  // ============================================================================
  // CLEANUP
  // ============================================================================

  /**
   * Destroy legal compliance engine
   */
  destroy(): void {
    if (this.validationTimer) {
      clearTimeout(this.validationTimer);
    }
    
    this.complianceCache.clear();
    
    console.log('[LegalComplianceEngine] Destroyed');
  }
}
